import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMsg, downloadFile } from "@/lib/api";
import { PageTitle, Panel, Btn, Sel } from "@/admin/ui";
import RuleDialog, { blankRule } from "@/admin/RuleDialog";
import Sections from "@/admin/Sections";

const KINDS = ["heading", "unclassified", "rule", "interpretation", "explanation", "example", "biographical", "background", "general", "artefact", "toc"];
const DOC_KEYS = [["doc1", "Doc 1 · Know Thy Name Know Thy Person"], ["doc2", "Doc 2 · Know the Name Know How to Connect"], ["doc3", "Doc 3 · Know Thy Name Know Thy Spirit"], ["other", "Other"]];

function UploadForm({ onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [key, setKey] = useState("doc1");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file); fd.append("title", title || file.name); fd.append("doc_key", key);
    setBusy(true);
    try { const r = await api.post("/admin/documents", fd); toast.success(`Imported ${r.data.passage_count} passages (version ${r.data.version})`); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Upload Word document (.docx)" testId="upload-panel">
      <div className="grid gap-3 sm:grid-cols-4">
        <Input type="file" accept=".docx" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file-input" />
        <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title-input" />
        <Sel value={key} onChange={setKey} options={DOC_KEYS} testId="doc-key-select" />
        <Btn disabled={!file || busy} onClick={submit} data-testid="doc-upload-btn"><Upload className="h-4 w-4" /> {busy ? "Importing…" : "Upload & extract"}</Btn>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Originals are stored immutably. Re-uploading a revised document creates a new version; earlier versions and passages are never deleted.</p>
    </Panel>
  );
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [pass, setPass] = useState({ total: 0, items: [] });
  const [ruleFrom, setRuleFrom] = useState(null);
  const loadDocs = () => api.get("/admin/documents").then((r) => setDocs(r.data));
  const loadPass = useCallback(() => api.get("/admin/passages", { params: { document_id: docId || undefined, kind: kind || undefined, q: q || undefined, skip, limit: 50 } }).then((r) => setPass(r.data)), [docId, kind, q, skip]);
  useEffect(() => { loadDocs(); }, []);
  useEffect(() => { loadPass(); }, [loadPass]);
  const setPassageKind = async (id, k) => { await api.patch(`/admin/passages/${id}`, { kind: k }); loadPass(); };

  return (
    <div className="space-y-6">
      <PageTitle title="Source Documents" sub="The three reference documents are parsed into passages with their chapter/section context. Classify each passage so that methodology is kept separate from examples and background." />
      <UploadForm onDone={() => { loadDocs(); loadPass(); }} />
      <Panel title="Documents" testId="documents-list">
        {!docs.length && <p className="text-sm text-muted-foreground" data-testid="documents-empty">No documents uploaded yet.</p>}
        <ul className="divide-y divide-border">
          {docs.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
              <span><strong>{d.title}</strong> <span className="text-muted-foreground">· {d.doc_key} · v{d.version} · {d.passage_count} passages · {d.uploaded_at.slice(0, 10)}</span></span>
              <button onClick={() => downloadFile(`/admin/documents/${d.id}/download`, d.filename)} className="inline-flex items-center gap-1 text-xs text-primary" data-testid={`download-doc-${d.id}`}><Download className="h-3.5 w-3.5" /> Original</button>
            </li>
          ))}
        </ul>
      </Panel>
      <Sections docId={docId} />
      <Panel title={`Passages (${pass.total})`} testId="passages-panel">
        <div className="mb-4 flex flex-wrap gap-2">
          <Sel value={docId} onChange={(v) => { setDocId(v); setSkip(0); }} options={[["", "All documents"], ...docs.map((d) => [d.id, `${d.title} v${d.version}`])]} testId="passage-doc-filter" />
          <Sel value={kind} onChange={(v) => { setKind(v); setSkip(0); }} options={[["", "All kinds"], ...KINDS]} testId="passage-kind-filter" />
          <Input placeholder="Search text" value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} className="max-w-xs" data-testid="passage-search-input" />
        </div>
        <div className="space-y-2">
          {pass.items.map((p) => (
            <div key={p.id} className="rounded border border-border p-3" data-testid={`passage-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="font-mono-ui">#{p.index} · {p.doc_key} · {(p.section_path || []).join(" › ") || "—"}</span>
                <span className="flex items-center gap-2">
                  <Sel value={p.kind} onChange={(k) => setPassageKind(p.id, k)} options={KINDS} className="h-7 text-xs" testId={`passage-kind-${p.id}`} />
                  <button onClick={() => setRuleFrom(blankRule({ document_id: p.document_id, document_title: p.doc_title, doc_key: p.doc_key, section: (p.section_path || []).join(" › "), passage_ids: [p.id] }))} className="text-primary hover:underline" data-testid={`rule-from-passage-${p.id}`}>Create rule</button>
                </span>
              </div>
              <p className={`mt-2 text-sm leading-relaxed ${p.kind === "heading" ? "font-semibold" : ""}`}>{p.text}</p>
            </div>
          ))}
        </div>
        {pass.total > 50 && (
          <div className="mt-4 flex gap-2">
            <Btn variant="ghost" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - 50))}>Previous</Btn>
            <Btn variant="ghost" disabled={skip + 50 >= pass.total} onClick={() => setSkip(skip + 50)}>Next</Btn>
          </div>
        )}
      </Panel>
      {ruleFrom && <RuleDialog rule={ruleFrom} open onClose={() => setRuleFrom(null)} onSaved={() => setRuleFrom(null)} />}
    </div>
  );
}
